import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";
import type { CompanyDetail } from "@upclaim/sdk/src/data-contracts";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrentTenant } from "@/features/tenant/api/use-current-tenant";
import { useTenants } from "@/features/tenants/api/use-tenants";
import { useCurrentUser } from "@/hooks/use-current-user";

import type { BusinessTravelCompany } from "../api/use-companies";
import { getCompanyTenantId } from "../lib/companies-rows";
import { CompanyConfigurationTab } from "./company-configuration-tab";
import { CompanyLegalSheet } from "./company-legal-sheet";

interface CompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId?: string | null;
  /** Preloaded (partial) company from the row — the detail is lazy-fetched on open. */
  company?: CompanyDetail | null;
  relation?: { travelAgencyId: string } | { travelAgencyCompanyId: string } | { airlineId: string };
  /**
   * The business travel company behind the legal entity. Present from the companies list,
   * absent when the sheet is opened for an airline — which then shows the legal sheet alone.
   */
  travelAgencyCompany?: BusinessTravelCompany | null;
}

/**
 * Company sheet. For a business travel company it opens on the settings driving its PNR
 * matching; for an airline it is the legal sheet alone.
 */
export function CompanyModal({
  open,
  onOpenChange,
  companyId,
  company,
  relation,
  travelAgencyCompany,
}: CompanyModalProps) {
  const { can } = useCurrentUser();
  const isUpclaimAdmin = can(UPCLAIM_ROLES);

  const [tab, setTab] = useState("configuration");

  const { data: tenants = [] } = useTenants(isUpclaimAdmin);
  const { data: currentTenant } = useCurrentTenant();

  const tenantId = travelAgencyCompany ? getCompanyTenantId(travelAgencyCompany) : null;
  const amadeusLinkType = isUpclaimAdmin
    ? (tenants.find((tenant) => tenant.id === tenantId)?.amadeusLinkType ?? null)
    : (currentTenant?.amadeusLinkType ?? null);

  const legalSheet = (
    <CompanyLegalSheet
      open={open}
      companyId={companyId}
      company={company}
      relation={relation}
      onSaved={() => onOpenChange(false)}
      footerSlot={<DialogClose render={<Button variant="outline" />}>Fermer</DialogClose>}
    />
  );

  if (!travelAgencyCompany) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {companyId ? "Fiche entreprise" : "Créer une nouvelle fiche entreprise"}
            </DialogTitle>
          </DialogHeader>
          <DialogPanel>{legalSheet}</DialogPanel>
        </DialogContent>
      </Dialog>
    );
  }

  const isDeactivated =
    Boolean(travelAgencyCompany.disabledAt) && new Date(travelAgencyCompany.disabledAt) <= new Date();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {travelAgencyCompany.displayName || "Entreprise sans nom"}
            <Badge variant={isDeactivated ? "error" : "success"}>
              {isDeactivated ? "Désactivée" : "Active"}
            </Badge>
          </DialogTitle>
          <p className="text-sm text-gray-500">
            {[
              travelAgencyCompany.travelAgency?.name,
              travelAgencyCompany.domain,
              travelAgencyCompany.bluebizIdentifier ? `OSI ${travelAgencyCompany.bluebizIdentifier}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </DialogHeader>

        <DialogPanel>
          <Tabs value={tab} onValueChange={(value) => setTab(String(value))}>
            <TabsList>
              <TabsTrigger value="configuration">Configuration</TabsTrigger>
              <TabsTrigger value="legal">Fiche société</TabsTrigger>
            </TabsList>

            <TabsContent value="configuration" className="pt-4">
              <CompanyConfigurationTab company={travelAgencyCompany} amadeusLinkType={amadeusLinkType} />
            </TabsContent>

            <TabsContent value="legal" className="pt-4">
              {legalSheet}
            </TabsContent>
          </Tabs>
        </DialogPanel>
      </DialogContent>
    </Dialog>
  );
}
